import {
  AppstoreOutlined,
  ApartmentOutlined,
  BarChartOutlined,
  CloudUploadOutlined,
  CustomerServiceOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  HomeOutlined,
  LogoutOutlined,
  MailOutlined,
  SearchOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import type { CmsContent } from "@innovate/cms-contract/legacy-content";
import {
  cmsAdminNavigation,
  cmsAdminNavMeta,
  type CmsAdminUser,
  type CmsAdminNavigationGroupKey,
  type CmsAdminNavigationItem,
  type CmsAdminNavKey,
} from "@innovate/cms-contract";
import {
  logoutCmsAdminAction,
} from "@innovate/cms-admin-ui";
import {
  App,
  Badge,
  Button,
  Dropdown,
  Layout,
  Menu,
  Modal,
  Space,
  Tag,
  Typography,
  type MenuProps,
} from "antd";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  EditorDraftProvider,
  type EditorDraftContextValue,
  type EditorSaveHandler,
} from "./EditorDraftContext";

type PendingPageChange = {
  destinationLabel: string;
  proceed: () => void | Promise<void>;
};

const HomeEditor = lazy(async () => ({ default: (await import("./HomeEditor")).HomeEditor }));
const ProductsEditor = lazy(async () => ({ default: (await import("./ProductsEditor")).ProductsEditor }));
const AboutInnovateEditor = lazy(async () => ({ default: (await import("./AboutInnovateEditor")).AboutInnovateEditor }));
const WhyInnovatePageEditor = lazy(async () => ({ default: (await import("./WhyInnovatePageEditor")).WhyInnovatePageEditor }));
const CoreTechnologiesEditor = lazy(async () => ({ default: (await import("./CoreTechnologiesEditor")).CoreTechnologiesEditor }));
const TechnologyPlatformEditor = lazy(async () => ({ default: (await import("./TechnologyPlatformEditor")).TechnologyPlatformEditor }));
const ServiceCapabilityEditor = lazy(async () => ({ default: (await import("./ServiceCapabilityEditor")).ServiceCapabilityEditor }));
const ApplicationsEditor = lazy(async () => ({ default: (await import("./ApplicationsEditor")).ApplicationsEditor }));
const SiteFooterEditor = lazy(async () => ({ default: (await import("./SiteFooterEditor")).SiteFooterEditor }));
const PrivacyPolicyEditor = lazy(async () => ({ default: (await import("./PrivacyPolicyEditor")).PrivacyPolicyEditor }));
const SupportEditor = lazy(async () => ({ default: (await import("./SupportEditor")).SupportEditor }));
const SeoPanel = lazy(async () => ({ default: (await import("./DataPanels")).SeoPanel }));
const ContactsPanel = lazy(async () => ({ default: (await import("./DataPanels")).ContactsPanel }));
const AnalyticsPanel = lazy(async () => ({ default: (await import("./DataPanels")).AnalyticsPanel }));
const AdminAccountsPanel = lazy(async () => ({ default: (await import("./AdminAccountsPanel")).AdminAccountsPanel }));

const navigationIcons: Record<CmsAdminNavigationGroupKey, ReactNode> = {
  home: <HomeOutlined />,
  about: <TeamOutlined />,
  applications: <ApartmentOutlined />,
  products: <ShoppingOutlined />,
  technology: <ExperimentOutlined />,
  services: <CustomerServiceOutlined />,
  settings: <SettingOutlined />,
  seo: <SearchOutlined />,
  server: <DatabaseOutlined />,
};

const homeTabByNavigation: Partial<Record<CmsAdminNavKey, string>> = {
  "home.banner": "hero",
  "home.company": "company",
  "home.connectivity": "connectivity",
  "home.applications": "applications",
  "home.emergency": "response",
  "home.technology": "technology",
};

function canAccessNavigationItem(item: CmsAdminNavigationItem, user: CmsAdminUser) {
  return item.key === "settings.accounts"
    ? user.can_manage_admins
    : user.is_superuser || user.editable_modules.includes(item.moduleKey);
}

function filterNavigationItems(
  items: CmsAdminNavigationItem[],
  user: CmsAdminUser,
): CmsAdminNavigationItem[] {
  return items.flatMap((item) => {
    if (!canAccessNavigationItem(item, user)) return [];
    if (!item.children) return [item];
    const children = filterNavigationItems(item.children, user);
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}

function firstNavigationLeaf(items: CmsAdminNavigationItem[]): CmsAdminNavKey | undefined {
  for (const item of items) {
    if (item.children?.length) {
      const nested = firstNavigationLeaf(item.children);
      if (nested) return nested;
      continue;
    }
    return item.key as CmsAdminNavKey;
  }
  return undefined;
}

function navigationContains(items: CmsAdminNavigationItem[], key: CmsAdminNavKey): boolean {
  return items.some((item) =>
    item.key === key || (item.children ? navigationContains(item.children, key) : false),
  );
}

function collectNavigationOpenKeys(items: CmsAdminNavigationItem[]): string[] {
  return items.flatMap((item) =>
    item.children?.length
      ? [item.key, ...collectNavigationOpenKeys(item.children)]
      : [],
  );
}

function navigationItemIcon(key: string): ReactNode {
  if (key === "server.contacts") return <MailOutlined />;
  if (key === "server.analytics") return <BarChartOutlined />;
  if (key.startsWith("applications.")) return <AppstoreOutlined />;
  return undefined;
}

function buildNavigationMenuItems(
  items: CmsAdminNavigationItem[],
  moduleCounts: Partial<Record<CmsAdminNavKey, number>>,
): NonNullable<MenuProps["items"]> {
  return items.map((item) => {
    const count = moduleCounts[item.key as CmsAdminNavKey];
    return {
      key: item.key,
      icon: navigationItemIcon(item.key),
      label: (
        <span className="cms-menu-label">
          <span>{item.label}</span>
          {count !== undefined ? <Badge count={count} showZero color="#64748b" /> : null}
        </span>
      ),
      ...(item.children?.length
        ? { children: buildNavigationMenuItems(item.children, moduleCounts) }
        : {}),
    };
  });
}

export function InnovateAdminDashboard({
  content,
  user,
  publishedAt,
  hasUnpublished,
  publishing,
  onPublish,
  onOpenVersions,
}: {
  content: CmsContent;
  user: CmsAdminUser;
  publishedAt: string | null;
  hasUnpublished: boolean;
  publishing: boolean;
  onPublish: () => Promise<void>;
  onOpenVersions: () => void;
}) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const visibleNavigation = useMemo(() => cmsAdminNavigation
    .map((group) => ({
      ...group,
      children: filterNavigationItems(group.children, user),
    }))
    .filter((group) => group.children.length > 0), [user]);
  const firstVisibleNav = firstNavigationLeaf(visibleNavigation[0]?.children ?? [])
    ?? (user.can_manage_admins ? "settings.accounts" : "home.banner");
  const [activeSection, setActiveSection] = useState<CmsAdminNavKey>(firstVisibleNav);
  const [selectedProductId, setSelectedProductId] = useState<string>();
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [pendingPageChange, setPendingPageChange] = useState<PendingPageChange | null>(null);
  const [savingBeforePageChange, setSavingBeforePageChange] = useState(false);
  const hasLocalChangesRef = useRef(false);
  const editorSaveRef = useRef<EditorSaveHandler | null>(null);
  const editorDiscardRef = useRef<(() => void) | null>(null);
  const saveInFlightRef = useRef<Promise<void> | null>(null);
  const meta = cmsAdminNavMeta[activeSection];
  const activeGroup = visibleNavigation.find((group) =>
    navigationContains(group.children, activeSection),
  ) ?? (activeSection === "products.detail"
    ? visibleNavigation.find((group) => group.key === "products")
    : undefined);
  const moduleCounts = useMemo<Partial<Record<CmsAdminNavKey, number>>>(() => ({
    "home.banner": content.home?.heroSlides?.length ?? 0,
    "home.company": 1,
    "home.connectivity": content.home?.connectivitySlides?.length ?? 0,
    "home.applications": content.home?.applicationSlides?.length ?? 0,
    "home.emergency": content.home?.responseSlides?.length ?? 0,
    "home.technology": content.home?.technology?.items?.length ?? 0,
    "products.catalog": 1,
    "products.groups": content.productsPage?.groups?.length ?? 0,
    "products.list": content.products?.length ?? 0,
    "about.hero": content.aboutUs?.hero?.slides?.length ?? 0,
    "about.introduction": 1,
    "about.focus-copy": 1,
    "about.focus-items": content.aboutUs?.focus?.items?.length ?? 0,
    "about.services-copy": 1,
    "about.services-items": content.aboutUs?.services?.items?.length ?? 0,
    "about.integration": 1,
    "about.reach-copy": 1,
    "about.reach-items": content.aboutUs?.reach?.items?.length ?? 0,
    "why-innovate.hero": content.whyInnovatePage?.hero?.slides?.length ?? 0,
    "why-innovate.capabilities-copy": 1,
    "why-innovate.capabilities-items": content.whyInnovatePage?.capabilities?.items?.length ?? 0,
    "why-innovate.platform-copy": 1,
    "why-innovate.platform-items": content.whyInnovatePage?.technologyPlatform?.items?.length ?? 0,
    "why-innovate.simulation": content.whyInnovatePage?.simulation?.slides?.length ?? 0,
    "why-innovate.team-copy": 1,
    "why-innovate.team-metrics": content.whyInnovatePage?.team?.metrics?.length ?? 0,
    "why-innovate.deployment-copy": 1,
    "why-innovate.deployment-steps": content.whyInnovatePage?.deployment?.steps?.length ?? 0,
    "core-technologies.hero": 1,
    "core-technologies.overview": 1,
    "core-technologies.pillars-copy": 1,
    "core-technologies.pillars-items": content.coreTechnologies?.pillars?.items?.length ?? 0,
    "core-technologies.advantages-copy": 1,
    "core-technologies.advantages-items": content.coreTechnologies?.advantages?.items?.length ?? 0,
    "technology-platform.hero": 1,
    "technology-platform.engineering": 1,
    "technology-platform.simulation-copy": 1,
    "technology-platform.simulation-items": content.technologyPlatform?.simulation?.items?.length ?? 0,
    "technology-platform.pillars-copy": 1,
    "technology-platform.pillars-items": content.technologyPlatform?.pillars?.items?.length ?? 0,
    "technology-platform.applications-copy": 1,
    "technology-platform.applications-items": content.technologyPlatform?.applications?.items?.length ?? 0,
    "technology-platform.platform": 1,
    "service-capability.hero": 1,
    "service-capability.sections": content.serviceCapability?.sections?.length ?? 0,
    "service-capability.end-to-end": 1,
    "applications.scenarios.hero": content.applicationScenarios?.hero?.slides?.length ?? 0,
    "applications.scenarios.domains-copy": 1,
    "applications.scenarios.domains-items": content.applicationScenarios?.operationalDomains?.items?.length ?? 0,
    "applications.scenarios.featured": content.applicationScenarios?.featured?.items?.length ?? 0,
    "applications.typical.hero": content.typicalApplications?.hero?.slides?.length ?? 0,
    "applications.typical.network-copy": 1,
    "applications.typical.network-metrics": content.typicalApplications?.network?.metrics?.length ?? 0,
    "applications.typical.solutions-copy": 1,
    "applications.typical.solutions-items": content.typicalApplications?.technicalSolutions?.items?.length ?? 0,
    "support.hero": 1,
    "support.faq-intro": 1,
    "support.faq-tags": content.support?.faqTags?.length ?? 0,
    "support.faq-items": content.support?.faqItems?.length ?? 0,
    "support.platform": 1,
    "settings.footer": 1,
    "settings.privacy.title": 1,
    "settings.privacy.sections": content.privacyPolicy?.sections?.length ?? 0,
    "settings.privacy.contact": 1,
  }), [content]);

  const markDirty = useCallback(() => {
    hasLocalChangesRef.current = true;
    setHasLocalChanges(true);
  }, []);

  const markClean = useCallback(() => {
    hasLocalChangesRef.current = false;
    setHasLocalChanges(false);
  }, []);

  const registerEditorSave = useCallback((handler: EditorSaveHandler) => {
    editorSaveRef.current = handler;
    return () => {
      if (editorSaveRef.current === handler) editorSaveRef.current = null;
    };
  }, []);

  const registerEditorDiscard = useCallback((handler: () => void) => {
    editorDiscardRef.current = handler;
    return () => {
      if (editorDiscardRef.current === handler) editorDiscardRef.current = null;
    };
  }, []);

  const discardCurrentDraft = useCallback(({
    showMessage = true,
  }: { showMessage?: boolean } = {}) => {
    editorDiscardRef.current?.();
    markClean();
    if (showMessage) message.info("未保存的页面修改已放弃");
  }, [markClean, message]);

  const saveCurrentDraft = useCallback(
    ({ showSuccess = true }: { showSuccess?: boolean } = {}) => {
      if (!hasLocalChangesRef.current) return Promise.resolve();
      if (saveInFlightRef.current) return saveInFlightRef.current;

      const handler = editorSaveRef.current;
      if (!handler) {
        message.error("当前编辑器无法保存，请刷新页面后重试。");
        return Promise.reject(new Error("No active CMS editor save handler."));
      }

      const task = (async () => {
        setSavingDraft(true);
        try {
          await handler();
          markClean();
          if (showSuccess) message.success("草稿已保存，可发布更新");
        } finally {
          setSavingDraft(false);
        }
      })();
      saveInFlightRef.current = task;
      const clearTask = () => {
        if (saveInFlightRef.current === task) saveInFlightRef.current = null;
      };
      void task.then(clearTask, clearTask);
      return task;
    },
    [markClean, message],
  );

  const draftContext = useMemo<EditorDraftContextValue>(
    () => ({
      discardCurrentDraft,
      hasLocalChanges,
      markClean,
      markDirty,
      registerEditorDiscard,
      registerEditorSave,
      saveCurrentDraft,
    }),
    [
      discardCurrentDraft,
      hasLocalChanges,
      markClean,
      markDirty,
      registerEditorDiscard,
      registerEditorSave,
      saveCurrentDraft,
    ],
  );

  const requestPageChange = useCallback((
    destinationLabel: string,
    proceed: () => void | Promise<void>,
  ) => {
    if (!hasLocalChangesRef.current) {
      void proceed();
      return true;
    }
    setPendingPageChange({ destinationLabel, proceed });
    return false;
  }, []);

  const navigateTo = useCallback(
    (nextSection: CmsAdminNavKey) => {
      if (nextSection === activeSection || savingDraft) return;
      requestPageChange(cmsAdminNavMeta[nextSection].title, () => {
        setActiveSection(nextSection);
      });
    },
    [activeSection, requestPageChange, savingDraft],
  );

  const openProduct = useCallback((productId: string) => {
    requestPageChange(cmsAdminNavMeta["products.detail"].title, () => {
      setSelectedProductId(productId);
      setActiveSection("products.detail");
    });
  }, [requestPageChange]);

  const createProduct = useCallback((productId: string) => {
    setSelectedProductId(productId);
    setActiveSection("products.detail");
  }, []);

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasLocalChangesRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, []);

  useEffect(() => {
    const isVisible = visibleNavigation.some((group) =>
      navigationContains(group.children, activeSection),
    );
    if (!isVisible && activeSection !== "products.detail") {
      setActiveSection(firstVisibleNav);
    }
  }, [activeSection, firstVisibleNav, visibleNavigation]);

  useEffect(() => {
    const showCleanupWarning = (event: Event) => {
      const failed = (event as CustomEvent<{ failed?: number }>).detail?.failed ?? 0;
      message.warning(`草稿操作已完成，但有 ${failed} 个未引用资源清理失败。`);
    };
    window.addEventListener("cms:asset-cleanup-warning", showCleanupWarning);
    return () => window.removeEventListener("cms:asset-cleanup-warning", showCleanupWarning);
  }, [message]);

  const confirmPublish = () => {
    modal.confirm({
      title: "发布全部待发布内容？",
      content: "只会发布已经保存到服务器的草稿，不会代为保存当前页面修改。",
      okText: "发布",
      onOk: async () => {
        await onPublish();
      },
    });
  };

  const logout = async () => {
    requestPageChange("退出登录", async () => {
      await logoutCmsAdminAction();
      queryClient.setQueryData(["me"], null);
    });
  };

  const cancelPendingPageChange = () => {
    if (!savingBeforePageChange) setPendingPageChange(null);
  };

  const discardAndProceed = async () => {
    if (!pendingPageChange || savingBeforePageChange) return;
    const { proceed } = pendingPageChange;
    discardCurrentDraft({ showMessage: false });
    setPendingPageChange(null);
    await proceed();
    message.info("未保存的页面修改已放弃");
  };

  const saveAndProceed = async () => {
    if (!pendingPageChange || savingBeforePageChange) return;
    const { proceed } = pendingPageChange;
    setSavingBeforePageChange(true);
    try {
      await saveCurrentDraft({ showSuccess: false });
      setPendingPageChange(null);
      await proceed();
      message.success("页面修改已保存");
    } catch {
      // 保存错误已在统一保存链路中提示；保留弹窗和当前编辑上下文。
    } finally {
      setSavingBeforePageChange(false);
    }
  };

  return (
    <EditorDraftProvider value={draftContext}>
    <Layout className="cms-shell">
      <header className="cms-topbar">
        <div className="cms-brand">
          <div className="cms-brand-icon">
            <img
              src={`${import.meta.env.BASE_URL}favicon.png`}
              alt=""
              aria-hidden="true"
            />
          </div>
          <div>
            <div className="cms-brand-title">INNOVATE CMS</div>
            <div className="cms-brand-subtitle">Content operations</div>
          </div>
        </div>
        <div className="cms-topbar-main">
          <div className="cms-current-page">
            <div>
              <span>{activeGroup?.label}</span>
              <h1>{meta.title}</h1>
            </div>
            <Tag color={hasUnpublished || hasLocalChanges ? "orange" : "green"}>
              {hasLocalChanges
                ? "有未保存修改"
                : hasUnpublished
                  ? "有未发布修改"
                  : "已同步发布"}
            </Tag>
          </div>
          <Space wrap>
            <Typography.Text type="secondary" className="cms-published-at">
              最近发布：{publishedAt ? new Date(publishedAt).toLocaleString("zh-CN", { hour12: false }) : "尚未发布"}
            </Typography.Text>
            <Button icon={<HistoryOutlined />} onClick={onOpenVersions}>
              版本
            </Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              loading={publishing || savingDraft}
              disabled={!user.can_publish || hasLocalChanges || !hasUnpublished}
              onClick={confirmPublish}
            >
              发布
            </Button>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  ...(user.can_manage_admins ? [{ key: "accounts", icon: <TeamOutlined />, label: "管理员账号" }] : []),
                  { key: "logout", icon: <LogoutOutlined />, danger: true, label: "退出登录" },
                ],
                onClick: ({ key }) => {
                  if (key === "accounts") navigateTo("settings.accounts");
                  if (key === "logout") void logout();
                },
              }}
            >
              <Button icon={<UserOutlined />}>{user.display_name || user.username}</Button>
            </Dropdown>
          </Space>
        </div>
      </header>

      <Layout className="cms-workspace">
        <Layout.Sider
          className="cms-sider"
          theme="light"
          width={300}
          breakpoint="lg"
          collapsedWidth={0}
        >
          <Menu
            mode="inline"
            selectedKeys={[activeSection === "products.detail" ? "products.list" : activeSection]}
            defaultOpenKeys={visibleNavigation.flatMap((group) => [
              `group:${group.key}`,
              ...collectNavigationOpenKeys(group.children),
            ])}
            onClick={({ key }) => {
              if (key in cmsAdminNavMeta) navigateTo(key as CmsAdminNavKey);
            }}
            items={visibleNavigation.map((group) => ({
              key: `group:${group.key}`,
              icon: navigationIcons[group.key],
              label: group.label,
              children: buildNavigationMenuItems(group.children, moduleCounts),
            }))}
          />
        </Layout.Sider>
        <Layout.Content className="cms-content">
          <div className="cms-content-inner">
            <div className="cms-editor-panel">
              <Suspense fallback={<div className="cms-route-loading">正在加载模块…</div>}>
              {activeSection.startsWith("home.") ? (
                <HomeEditor content={content} activeSection={homeTabByNavigation[activeSection]} />
              ) : null}
              {activeSection.startsWith("products.") ? (
                <ProductsEditor
                  content={content}
                  selectedProductId={selectedProductId}
                  view={
                    activeSection === "products.catalog"
                      ? "catalog"
                      : activeSection === "products.groups"
                        ? "groups"
                        : activeSection === "products.detail"
                          ? "detail"
                          : "list"
                  }
                  onCreateProduct={createProduct}
                  onOpenProduct={openProduct}
                  onReturnToList={() => navigateTo("products.list")}
                />
              ) : null}
              {activeSection.startsWith("about.") ? (
                <AboutInnovateEditor
                  content={content}
                  activeSection={activeSection.slice("about.".length)}
                />
              ) : null}
              {activeSection.startsWith("why-innovate.") ? (
                <WhyInnovatePageEditor
                  content={content}
                  activeSection={activeSection.slice("why-innovate.".length)}
                />
              ) : null}
              {activeSection.startsWith("core-technologies.") ? (
                <CoreTechnologiesEditor
                  content={content}
                  activeSection={activeSection.slice("core-technologies.".length)}
                />
              ) : null}
              {activeSection.startsWith("technology-platform.") ? (
                <TechnologyPlatformEditor
                  content={content}
                  activeSection={activeSection.slice("technology-platform.".length)}
                />
              ) : null}
              {activeSection.startsWith("service-capability.") ? (
                <ServiceCapabilityEditor
                  content={content}
                  activeSection={activeSection.slice("service-capability.".length)}
                />
              ) : null}
              {activeSection.startsWith("applications.") ? (
                <ApplicationsEditor
                  content={content}
                  activePage={activeSection.startsWith("applications.typical.") ? "typical-applications" : "application-scenarios"}
                  activeSection={activeSection.split(".").at(-1)}
                />
              ) : null}
              {activeSection === "settings.footer" ? (
                <SiteFooterEditor content={content} />
              ) : null}
              {activeSection.startsWith("settings.privacy.") ? (
                <PrivacyPolicyEditor
                  content={content}
                  activeSection={activeSection.slice("settings.privacy.".length)}
                />
              ) : null}
              {activeSection === "settings.accounts" ? <AdminAccountsPanel /> : null}
              {activeSection.startsWith("support.") ? (
                <SupportEditor
                  content={content}
                  activeSection={activeSection.slice("support.".length)}
                />
              ) : null}
              {activeSection === "seo.configs" ? <SeoPanel /> : null}
              {activeSection === "server.contacts" ? <ContactsPanel /> : null}
              {activeSection === "server.analytics" ? <AnalyticsPanel /> : null}
              </Suspense>
            </div>
          </div>
        </Layout.Content>
      </Layout>
      <Modal
        title="当前页面有未保存修改"
        open={Boolean(pendingPageChange)}
        closable={!savingBeforePageChange}
        maskClosable={!savingBeforePageChange}
        keyboard={!savingBeforePageChange}
        onCancel={cancelPendingPageChange}
        footer={[
          <Button key="cancel" disabled={savingBeforePageChange} onClick={cancelPendingPageChange}>
            取消
          </Button>,
          <Button key="discard" danger disabled={savingBeforePageChange} onClick={() => void discardAndProceed()}>
            不保存并切换
          </Button>,
          <Button key="save" type="primary" loading={savingBeforePageChange} onClick={() => void saveAndProceed()}>
            保存并切换
          </Button>,
        ]}
      >
        即将前往“{pendingPageChange?.destinationLabel ?? "目标页面"}”。请选择如何处理当前修改。
      </Modal>
    </Layout>
    </EditorDraftProvider>
  );
}
